import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Typography,
  Tag,
  Popconfirm,
  Alert,
  Space,
  Empty,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import {
  listDemoSchools,
  deleteDemoSchool,
} from "../../../services/demo/demo.service";
import type { DemoSchoolListItem } from "../../../services/demo/demo.types";

const { Text } = Typography;

export interface DemoSchoolsListViewProps {
  refreshToken: number;
  onDeleted: () => void;
}

const DemoSchoolsListView: React.FC<DemoSchoolsListViewProps> = ({
  refreshToken,
  onDeleted,
}) => {
  const [schools, setSchools] = useState<DemoSchoolListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteFeedback, setDeleteFeedback] = useState<
    { type: "success" | "error"; text: string } | null
  >(null);

  const fetchSchools = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listDemoSchools();
      setSchools(data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount
  useEffect(() => {
    fetchSchools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when refreshToken changes (skip initial render — handled by mount effect)
  const isFirstRender = React.useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchSchools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const handleConfirmDelete = async (school: DemoSchoolListItem) => {
    setDeletingId(school.id);
    setDeleteFeedback(null);
    try {
      await deleteDemoSchool(school.id);
      setDeleteFeedback({
        type: "success",
        text: `Escuela "${school.name}" eliminada correctamente.`,
      });
      onDeleted();
    } catch {
      setDeleteFeedback({
        type: "error",
        text: "Error al eliminar la escuela demo. Intentá de nuevo.",
      });
    } finally {
      setDeletingId(null);
    }
  };

  const columns: ColumnsType<DemoSchoolListItem> = [
    {
      title: "Escuela",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Grados incluidos",
      dataIndex: "grades",
      key: "grades",
      render: (grades: string[]) => (
        <Space size={4} wrap>
          {grades.map((g) => (
            <Tag key={g}>{g}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "Último uso",
      dataIndex: "lastUsedAt",
      key: "lastUsedAt",
      render: (lastUsedAt: string | null) =>
        lastUsedAt === null ? (
          <Text type="secondary">Sin uso</Text>
        ) : (
          dayjs(lastUsedAt).format("DD/MM/YYYY")
        ),
    },
    {
      title: "Eliminar",
      key: "delete",
      render: (_: unknown, record: DemoSchoolListItem) => (
        <Popconfirm
          title="¿Eliminar escuela demo?"
          description={record.name}
          okText="Eliminar"
          okButtonProps={{ danger: true, loading: deletingId === record.id }}
          cancelText="Cancelar"
          onConfirm={() => handleConfirmDelete(record)}
        >
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            aria-label={`Eliminar escuela ${record.name}`}
          />
        </Popconfirm>
      ),
    },
  ];

  if (error) {
    return (
      <Space direction="vertical">
        <Text type="danger">Error al cargar las escuelas demo.</Text>
        <Button onClick={fetchSchools}>Reintentar</Button>
      </Space>
    );
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      {deleteFeedback && (
        <Alert
          type={deleteFeedback.type}
          message={deleteFeedback.text}
          closable
          afterClose={() => setDeleteFeedback(null)}
        />
      )}
      <Table<DemoSchoolListItem>
        columns={columns}
        dataSource={schools}
        rowKey="id"
        loading={loading}
        locale={{
          emptyText: (
            <Empty description="No hay escuelas demo creadas todavía" />
          ),
        }}
      />
    </Space>
  );
};

export default DemoSchoolsListView;
